import { Box, Flex, Heading, VStack } from "@chakra-ui/react"
import NextChakraLink from "components/_common/NextChakraLink"

export default function MegaMenu({ item, level, isLast, open, isMobile = false, mobileMenuToggle, closeMenus }) {
    // Extract mega menu sections from item structure with defaults
    const leftPanelContent = item.leftPanelContent || `<p>No content setup in the menu item</p>`

    const middleLinks = item.pages || []

    // Only subheads with children become columns; the divider goes on every column but the last.
    const isColumnSection = section => section.typeHandle === "subhead" && section.pages && section.pages.length > 0
    const lastColumnIndex = middleLinks.reduce((last, section, i) => (isColumnSection(section) ? i : last), -1)

    // Craft returns null for an untouched WYSIWYG field, but an edited-then-cleared one
    // can come back as "", "<p></p>" or "<p>&nbsp;</p>" — all of those count as empty.
    const hasRightPanel = hasHtmlContent(item.rightPanelContent)

    const menuItemColor = "#070A36"

    // Helper function to get link href
    const getLinkHref = link => {
        switch (link.typeHandle) {
            case "htmlPage":
                return "/" + link.page?.[0]?.slug
            case "path":
                return link.navigationPath ? "/" + link.navigationPath : "/"
            case "link":
                return link.string
            default:
                return "#"
        }
    }

    // Render a single link item
    const renderLinkItem = (link, index) => {
        const linkHref = getLinkHref(link)

        return (
            <NextChakraLink
                className="megaMenu__link"
                key={`link-${index}`}
                href={linkHref}
                label={link.title || link.label}
                py={0.5}
                fontSize="16px"
                color={menuItemColor}
                lineHeight="1.4"
                _hover={{ textDecoration: "none", color: "#00c6ff" }}
                onClick={() => {
                    mobileMenuToggle && mobileMenuToggle()
                    closeMenus && closeMenus()
                }}
            />
        )
    }

    return (
        <Box
            className="megaMenu"
            display={open ? "block" : "none"}
            position={isMobile ? "relative" : "absolute"}
            top={isMobile ? 0 : "99%"}
            left={0}
            width={"100%"}
            zIndex={999}
            bg="white"
            color="#333"
            boxShadow={isMobile ? "none" : "0 4px 12px rgba(0,0,0,0.15)"}
        >
            <Box className="megaMenu__wrapper" width="100%">
                <Flex direction={{ base: "column", md: "row" }} gap={0}>
                    {/* Left Section - Featured Content */}
                    {!isMobile && (
                        <Column className="megaMenu__leftPanel" width="35%" bgColor={menuItemColor} color="#fff">
                            <Box dangerouslySetInnerHTML={{ __html: leftPanelContent }} />
                        </Column>
                    )}

                    {/* Middle Section - Multi-tiered Links */}
                    <Column className="megaMenu__centerPanel" width={isMobile ? "100%" : hasRightPanel ? "40%" : "65%"}>
                        <Flex direction={isMobile ? "column" : "row"} gap={isMobile ? 1 : 6} wrap="wrap" height="100%">
                            {middleLinks.map((section, sectionIndex) => {
                                const isSubhead = section.typeHandle === "subhead"

                                // If it's a subhead with children pages, render the section
                                if (isColumnSection(section)) {
                                    return (
                                        <VStack
                                            key={`section-${sectionIndex}`}
                                            align="stretch"
                                            flex="1"
                                            minW="150px"
                                            height="100%"
                                            borderRight={
                                                !isMobile && sectionIndex !== lastColumnIndex ? "1px solid #eee" : null
                                            }
                                        >
                                            {!section.title.includes("[HIDE-SUBHEAD]") && (
                                                <Heading
                                                    as="h4"
                                                    fontSize="14px"
                                                    fontWeight="600"
                                                    color={menuItemColor}
                                                    mb={3}
                                                    textTransform="uppercase"
                                                >
                                                    {section.title}
                                                </Heading>
                                            )}

                                            {section.pages.map((link, linkIndex) => renderLinkItem(link, linkIndex))}
                                        </VStack>
                                    )
                                }

                                // If it's a standalone link (not a subhead), render it directly
                                if (!isSubhead) {
                                    return renderLinkItem(section, sectionIndex)
                                }

                                return null
                            })}
                        </Flex>
                    </Column>

                    {/* Right Section - Image */}
                    {!isMobile && hasRightPanel && (
                        <Column className="megaMenu__rightPanel" width="25%">
                            <Box dangerouslySetInnerHTML={{ __html: item.rightPanelContent }} />
                        </Column>
                    )}
                </Flex>
            </Box>
        </Box>
    )
}

function Column({ children, width = "33.3%", ...rest }) {
    return (
        <Box
            className="megaMenu__panel"
            width={{ base: "100%", md: width }}
            px={{ base: 4, md: 8 }}
            py={{ base: 3, md: 6 }}
            {...rest}
        >
            {children}
        </Box>
    )
}

// True when the WYSIWYG html holds something worth rendering. Media-only markup
// (an image, an embed) has no text but is still content.
function hasHtmlContent(html) {
    if (!html) return false
    if (/<(img|iframe|video|audio|svg|embed|object|picture|source|hr|input|button|canvas)\b/i.test(html)) return true
    return (
        html
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;|&#160;|&#xa0;/gi, " ")
            .trim() !== ""
    )
}
